import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Card, CardBody } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import Page from 'views/Layout/Pages/Page';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import { useAIAgentsHub } from '../AiAgentsHubContext';

interface VisiblePrompt {
  id: string;
  text: string;
  category: string;
}

interface VisiblePromptsResponse {
  prompts: VisiblePrompt[];
}

interface PromptsAdminPageProps {
  embedded?: boolean;
}

const PromptsAdminPage: FC<PromptsAdminPageProps> = ({ embedded = true }) => {
  const { setAction } = useAIAgentsHub();
  const { formatMessage } = useIntl();

  const { data, status } = useConfirmApi<VisiblePromptsResponse>({
    url: 'ai/agents/prompts',
    method: 'GET',
  });

  useEffect(() => {
    setAction(null);
  }, [setAction]);

  const groupedPrompts = useMemo(() => {
    const groups: Record<string, VisiblePrompt[]> = {};
    for (const prompt of data?.prompts ?? []) {
      if (!groups[prompt.category]) {
        groups[prompt.category] = [];
      }
      groups[prompt.category].push(prompt);
    }
    return groups;
  }, [data]);

  const handlePromptClick = useCallback((text: string) => {
    window.dispatchEvent(new CustomEvent('open-ai-chat', { detail: { text } }));
  }, []);

  if (status === 'LOADING') {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.ai.agents.prompts_admin.loading',
          defaultMessage: 'Loading prompts...',
        })}
      />
    );
  }

  const categories = Object.keys(groupedPrompts);

  return (
    <Page
      embedded={embedded}
      title={formatMessage({
        id: 'app.views.ai.agents.prompts_admin.title',
        defaultMessage: 'Prompt Suggestions',
      })}
    >
      {categories.length === 0 ? (
        <EmptyState
          title={formatMessage({
            id: 'app.views.ai.agents.prompts_admin.empty.title',
            defaultMessage: 'No prompts available',
          })}
          subtitle={formatMessage({
            id: 'app.views.ai.agents.prompts_admin.empty.subtitle',
            defaultMessage:
              'Prompt suggestions will appear here once agents with capabilities are published.',
          })}
        />
      ) : (
        <Card>
          <CardBody>
            <p className="text-muted mb-4">
              {formatMessage({
                id: 'app.views.ai.agents.prompts_admin.helper',
                defaultMessage:
                  'Click on any prompt below to open a new AI chat with that question ready to go.',
              })}
            </p>
            {categories.map((category) => (
              <div key={category} className="mb-4">
                <h5 className="mb-3">{category}</h5>
                <div className="list-group">
                  {groupedPrompts[category].map((prompt) => (
                    <div
                      key={prompt.id}
                      className="list-group-item list-group-item-action d-flex align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handlePromptClick(prompt.text)}
                    >
                      <i className="fe fe-message-circle me-3 text-muted" />
                      <span>{prompt.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </Page>
  );
};

export default React.memo(PromptsAdminPage);
